import React, { useCallback, useState } from 'react';
import {
  Box,
  FormControl,
  IconButton,
  Link,
  MenuItem,
  Select,
  SelectChangeEvent,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import SentimentScore from '../SentimentScore/SentimentScore';
import KeywordService from '../../services/KeywordService';
import LockIcon from '@mui/icons-material/Lock';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { IKeywordSearchResult } from '../../shared/interfaces/IKeywordSearchResult';

interface Props {
  sentiment_score: SentimentScore;
  link: string;
  displayedLink: string;
  title: string;
  snippet: string;
  position_overall: number;
  entry_id: string;
  id: string;
  sentiment_overridden: boolean;
  original_sentiment_score: string;
  entry: IKeywordSearchResult;
  reloadData: () => void;
}

const KeywordSearchResult = ({
  sentiment_score,
  link,
  displayedLink,
  title,
  snippet,
  position_overall,
  entry_id,
  id,
  sentiment_overridden,
  original_sentiment_score,
  entry,
  reloadData,
}: Props) => {
  const theme = useTheme();

  const handleChangeSentimentScore = useCallback(
    async (event: SelectChangeEvent) => {
      const newSentimentScore = event.target.value as SentimentScore;
      const controller = new AbortController();

      try {
        await KeywordService.setOverride(id, entry, newSentimentScore, 'url', controller.signal);
        reloadData();
      } catch (error) {
        console.error('Failed to update sentiment score:', error);
      }

      return () => {
        controller.abort();
      };
    },
    [id, entry_id]
  );

  const handleDeleteSentiment = useCallback(async () => {
    const controller = new AbortController();

    try {
      await KeywordService.deleteOverride(id, entry, controller.signal);
      reloadData();
    } catch (error) {
      console.error('Failed to delete sentiment override:', error);
    }

    return () => {
      controller.abort();
    };
  }, [id, entry_id, original_sentiment_score]);

  return (
    <Box
      sx={{
        display: 'flex',
        my: theme.spacing(2),
      }}
    >
      <Box
        sx={{
          display: 'flex',
          pt: theme.spacing(1),
          pr: theme.spacing(1),
          pl: theme.spacing(5),
        }}
      >
        <Typography variant="body1">{position_overall}</Typography>
      </Box>
      <Box
        sx={{ m: 1, display: 'flex', flexDirection: 'row', alignItems: 'center', width: '80px' }}
      >
        <FormControl
          fullWidth
          size="small"
          variant={'standard'}
        >
          <Select
            labelId="select-sentiment-score-label"
            id="select-sentiment-score-label"
            value={sentiment_score || ''}
            onChange={handleChangeSentimentScore}
            disableUnderline={true}
          >
            <MenuItem
              value={'UNDEFINED'}
              disabled={true}
            >
              <SentimentScore sentiment_score={'UNDEFINED'} />
            </MenuItem>
            <MenuItem value={'POSITIVE'}>
              <SentimentScore sentiment_score={'POSITIVE'} />
            </MenuItem>
            <MenuItem value={'NEGATIVE'}>
              <SentimentScore sentiment_score={'NEGATIVE'} />
            </MenuItem>
            <MenuItem value={'NEUTRAL'}>
              <SentimentScore sentiment_score={'NEUTRAL'} />
            </MenuItem>
          </Select>
        </FormControl>
      </Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          pt: theme.spacing(1),
          px: theme.spacing(1),
          maxWidth: 650,
        }}
      >
        <Typography
          variant="body1"
          sx={{
            color: theme.palette.primary.light,
          }}
        >
          {displayedLink}
        </Typography>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            mt: theme.spacing(1),
          }}
        >
          <Link
            href={link}
            underline="none"
            target="_blank"
            variant="body1"
            rel="noreferrer"
            sx={{ color: '#1976D2', fontWeight: 700 }}
          >
            {title}
          </Link>
        </Box>
        <Typography variant="subtitle2">{snippet}</Typography>
      </Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'center',
          ml: 'auto',
        }}
      >
        {sentiment_overridden ? (
          <>
            <Tooltip
              title={'Sentiment Score zurücksetzen'}
              arrow
            >
              <IconButton
                size="small"
                color="warning"
                sx={{ ml: 'auto' }}
                onClick={handleDeleteSentiment}
              >
                <RestartAltIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip
              title={'Sentiment Score wurde überschrieben'}
              arrow
            >
              <IconButton size={'small'}>
                <LockIcon
                  color="action"
                  aria-disabled
                />
              </IconButton>
            </Tooltip>
          </>
        ) : null}
      </Box>
    </Box>
  );
};

export default KeywordSearchResult;
